/**
 * Selector resolver for the debug bridge.
 *
 * Implements the layered priority documented in task 2902 Decision 2:
 *   1. `{ testId }`        — `[data-testid="..."]`
 *   2. `{ text, exact? }`  — visible text content (substring case-insensitive,
 *                            or strict equality with `exact: true`)
 *   3. `{ role, name }`    — implicit/explicit ARIA role + accessible name
 *   4. `{ css }`           — raw `document.querySelectorAll` (last resort)
 *
 * The resolver returns `HTMLElement[]`. The bridge enforces uniqueness for
 * click/fill ops and reports `selector_no_match` / `selector_ambiguous`
 * accordingly.
 */

import type { Selector } from './types'

// ── Visibility helper ───────────────────────────────────────────────────────

/**
 * An element is "visible" if it is connected to the document and no ancestor
 * is hidden via `display: none` or `visibility: hidden`.
 *
 * We walk ancestors with `getComputedStyle` rather than relying on
 * `offsetParent`. Browsers set `offsetParent === null` for `display: none`
 * descendants, but jsdom (used by Vitest) does not implement layout and
 * returns `null` for nearly every attached element regardless of CSS.
 * Walking computed style works in both environments.
 */
export function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (!el.isConnected) return false

  let cursor: HTMLElement | null = el
  while (cursor && cursor.nodeType === 1) {
    let style: CSSStyleDeclaration | undefined
    try {
      style = getComputedStyle(cursor)
    } catch {
      return false
    }
    if (style.display === 'none' || style.visibility === 'hidden') return false
    cursor = cursor.parentElement
  }
  return true
}

// ── Implicit role table ─────────────────────────────────────────────────────

/**
 * Map of role -> CSS selectors that imply that role natively.
 * Plus the explicit `[role="<role>"]` form is always considered.
 *
 * Keep this minimal — exhaustive role mapping (`aria-query` parity) is task
 * 2903's concern; the basics are sufficient here.
 */
const IMPLICIT_ROLE_SELECTORS: Record<string, string[]> = {
  button: ['button', 'input[type="button"]', 'input[type="submit"]', 'input[type="reset"]'],
  link: ['a[href]'],
  textbox: [
    'input:not([type])',
    'input[type="text"]',
    'input[type="email"]',
    'input[type="password"]',
    'input[type="search"]',
    'input[type="tel"]',
    'input[type="url"]',
    'input[type="number"]',
    'textarea',
  ],
  combobox: ['select'],
  checkbox: ['input[type="checkbox"]'],
  radio: ['input[type="radio"]'],
  switch: [],
  tab: [],
  menuitem: [],
}

// ── Accessible name resolution ──────────────────────────────────────────────

/**
 * Resolve an element's accessible name per the priority documented in
 * task 2902 Decision 2:
 *   1. `aria-label`
 *   2. `aria-labelledby` (concatenation of referenced elements' textContent)
 *   3. associated `<label for=...>` text (or wrapping `<label>` text)
 *   4. element's own textContent
 */
export function accessibleName(el: HTMLElement): string {
  const ariaLabel = el.getAttribute('aria-label')
  if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim()

  const labelledBy = el.getAttribute('aria-labelledby')
  if (labelledBy) {
    const ids = labelledBy.split(/\s+/).filter(Boolean)
    const parts: string[] = []
    for (const id of ids) {
      const ref = el.ownerDocument?.getElementById(id)
      if (ref) parts.push((ref.textContent ?? '').trim())
    }
    const joined = parts.join(' ').trim()
    if (joined) return joined
  }

  // <label for="<id>"> ... </label>
  if (el.id) {
    const label = el.ownerDocument?.querySelector<HTMLLabelElement>(
      `label[for="${cssEscape(el.id)}"]`,
    )
    if (label && label.textContent?.trim()) return label.textContent.trim()
  }

  // Wrapping <label>
  const wrappingLabel = el.closest('label')
  if (wrappingLabel && wrappingLabel.textContent?.trim()) {
    return wrappingLabel.textContent.trim()
  }

  return (el.textContent ?? '').trim()
}

// ── findByText ──────────────────────────────────────────────────────────────

/**
 * Find visible elements whose own text content matches `text`.
 *
 * - `exact: false` (default) → case-insensitive substring match.
 * - `exact: true`            → strict equality after trim, case-sensitive.
 * - Filters to leaf-most matches (no descendant of another match) so a
 *   `<button>Save</button>` is preferred over the `<div>` wrapping it.
 * - Filters out hidden elements (`isVisible`).
 */
export function findByText(text: string, exact: boolean): HTMLElement[] {
  const root = typeof document !== 'undefined' ? document : undefined
  if (!root) return []
  const target = exact ? text.trim() : text.trim().toLowerCase()
  const all = Array.from(root.querySelectorAll<HTMLElement>('*'))
  const matches: HTMLElement[] = []

  for (const el of all) {
    if (!isVisible(el)) continue
    const content = (el.textContent ?? '').trim()
    if (!content) continue

    if (exact) {
      if (content === target) matches.push(el)
    } else {
      if (content.toLowerCase().includes(target)) matches.push(el)
    }
  }

  // Filter to leaf-most matches: drop any match that contains another match.
  return matches.filter((el) => !matches.some((other) => other !== el && el.contains(other)))
}

// ── findByRoleAndName ───────────────────────────────────────────────────────

/**
 * Find visible elements with the given role (explicit or implicit) whose
 * accessible name matches `name` (substring, case-insensitive).
 */
export function findByRoleAndName(role: string, name: string): HTMLElement[] {
  const root = typeof document !== 'undefined' ? document : undefined
  if (!root) return []

  const selectors: string[] = []
  selectors.push(`[role="${cssEscape(role)}"]`)
  const implicit = IMPLICIT_ROLE_SELECTORS[role]
  if (implicit) selectors.push(...implicit)

  const seen = new Set<HTMLElement>()
  for (const sel of selectors) {
    let nodes: NodeListOf<HTMLElement>
    try {
      nodes = root.querySelectorAll<HTMLElement>(sel)
    } catch {
      continue
    }
    nodes.forEach((el) => seen.add(el))
  }

  const target = name.trim().toLowerCase()
  const matches: HTMLElement[] = []
  for (const el of seen) {
    if (!isVisible(el)) continue
    const accName = accessibleName(el).toLowerCase()
    if (accName.includes(target)) matches.push(el)
  }
  return matches
}

// ── Public entry point ──────────────────────────────────────────────────────

/**
 * Resolve a selector to the array of matching visible elements.
 * The bridge is responsible for enforcing uniqueness for ops that need it.
 */
export function resolve(selector: Selector): HTMLElement[] {
  const root = typeof document !== 'undefined' ? document : undefined
  if (!root) return []

  if ('testId' in selector) {
    const escaped = cssEscape(selector.testId)
    return Array.from(root.querySelectorAll<HTMLElement>(`[data-testid="${escaped}"]`)).filter(
      isVisible,
    )
  }
  if ('text' in selector) {
    return findByText(selector.text, selector.exact ?? false)
  }
  if ('role' in selector) {
    return findByRoleAndName(selector.role, selector.name)
  }
  // css
  try {
    return Array.from(root.querySelectorAll<HTMLElement>(selector.css)).filter(isVisible)
  } catch {
    return []
  }
}

// ── CSS.escape fallback ─────────────────────────────────────────────────────

/**
 * `CSS.escape` is available in jsdom 22+ and all modern browsers. Defensive
 * fallback (POSIX-style escaping for the small set of chars that matter for
 * `data-testid` and `id` values) for the rare environment that lacks it.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/["\\]/g, '\\$&')
}
